import React, { useState, useEffect, useMemo } from 'react';
import MedicalHistoryEditor from './MedicalHistoryEditor';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { fetchTemplate, fetchTemplates } from '../../../../services/surveys';
import { Translate } from 'react-localize-redux';
import { SmartFieldType } from '..';
import Modal from '../../../general/modal';

export interface Template {
    uuid: string;
    name: string;
    content: string;
}

export enum TemplatedMedicalHistoryEditorType {
    SURGERY_REPORT = 1,
}

interface TemplatedMedicalHistoryEditorViewProps {
    type:TemplatedMedicalHistoryEditorType,
    uuidInvestigation: string,
    updateElement: (index: number, element: SmartFieldType) => void;
    onBlur?: () => void;
    initialValue?: string;
}
interface TemplatedMedicalHistoryEditorProps extends TemplatedMedicalHistoryEditorViewProps {
    fetchTemplates: (uuidInvestigation: string, type:TemplatedMedicalHistoryEditorType) => Promise<Template[]>;
    fetchTemplate: (uuidInvestigation: string, templateId: string) => Promise<Template | null>;
}

const TemplatedMedicalHistoryEditor: React.FC<TemplatedMedicalHistoryEditorProps> = ({
    type,
    uuidInvestigation,
    updateElement,
    onBlur,
    initialValue
}) => {
    return <TemplatedMedicalHistoryEditorView type={type} uuidInvestigation={uuidInvestigation} 
        updateElement={updateElement} onBlur={onBlur} 
        fetchTemplates={fetchTemplates} fetchTemplate={fetchTemplate} initialValue={initialValue} />
}

export const TemplatedMedicalHistoryEditorView: React.FC<TemplatedMedicalHistoryEditorProps> = ({
    type,
    uuidInvestigation,
    fetchTemplates,
    fetchTemplate,
    updateElement,
    onBlur,
    initialValue
}) => {
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState<boolean>(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [templateContent, setTemplateContent] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [originalTemplateContent, setOriginalTemplateContent] = useState<string | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [pendingTemplateId, setPendingTemplateId] = useState<string>('');
    const [displayTemplateId, setDisplayTemplateId] = useState<string>('');
    const [displayTemplateIdBeforeChange, setDisplayTemplateIdBeforeChange] = useState<string>('');
    const [initialValueMatched, setInitialValueMatched] = useState<boolean>(false);

    useEffect(() => {
        const fetchTemplatesAsync = async () => {
            try {
                setLoadingTemplates(true);
                const templatesData = await fetchTemplates(uuidInvestigation, type)
                setTemplates(templatesData);
            } catch (error) {
                console.error('Failed to fetch templates:', error);
            }
            finally{
                setLoadingTemplates(false);
            }
        };

        fetchTemplatesAsync();
    }, []);

    // Handle initial value loading
    useEffect(() => {
        if (initialValue && templates.length > 0) {
            setTemplateContent(initialValue);
            // Try to find if the initial value corresponds to an existing template
            let matched = false;
            for (const template of templates) {
                if (template.content === initialValue) {
                    setSelectedTemplateId(template.uuid);
                    setDisplayTemplateId(template.uuid);
                    setOriginalTemplateContent(template.content);
                    setInitialValueMatched(true);
                    matched = true;
                    break;
                }
            }
            if (!matched) {
                // Initial value doesn't match any template, treat as modified
                setOriginalTemplateContent(null);
                setInitialValueMatched(false);
            }
        }
    }, [initialValue, templates]);

    useEffect(() => {
        async function loadTemplate() {
            // Only load template when selectedTemplateId changes
            // This effect does NOT run when showConfirmModal changes (removed from dependencies)
            if (selectedTemplateId) {
                setIsLoading(true);
                try {
                    const content = await fetchTemplate(uuidInvestigation, selectedTemplateId);
                    const templateContentValue = content?.content || '';
                    setTemplateContent(templateContentValue);
                    setOriginalTemplateContent(templateContentValue);
                    // Update displayTemplateId to match the loaded template
                    setDisplayTemplateId(selectedTemplateId);
                    updateElement(0, {
                        "template_medical_history-template_id": selectedTemplateId,
                       "template_medical_history-notes": templateContentValue
                   });
                } catch (error) {
                    console.error('Failed to load template:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        }

        loadTemplate();
    }, [selectedTemplateId]);

    const handleTemplateChange = (event: SelectChangeEvent) => {
        const newTemplateId = event.target.value;
        
        // Check if content has been modified from original template
        const hasBeenModified = originalTemplateContent !== null && 
                                templateContent !== null && 
                                templateContent !== originalTemplateContent;
        
        // If initial value didn't match any template, treat as modified
        const hasUnmatchedInitialValue = !initialValueMatched && initialValue !== null && initialValue !== undefined;
        
        // Only show modal if:
        // 1. We have a previously selected template AND content has been modified, OR
        // 2. We have an initial value that doesn't match any template (treat as modified)
        // Don't show modal on first template selection when there's no initial value
        const isModified = (selectedTemplateId && hasBeenModified) || hasUnmatchedInitialValue;
        
        if (isModified) {
            // Show confirmation modal - store current display value before changing it
            setDisplayTemplateIdBeforeChange(displayTemplateId);
            setDisplayTemplateId(newTemplateId);
            setPendingTemplateId(newTemplateId);
            setShowConfirmModal(true);
        } else {
            // No changes or first selection - load template directly
            setSelectedTemplateId(newTemplateId);
            setDisplayTemplateId(newTemplateId);
        }
    };

    const handleConfirmTemplateChange = async () => {
        setShowConfirmModal(false);
        setSelectedTemplateId(pendingTemplateId);
        setPendingTemplateId('');
    };

    const handleCancelTemplateChange = () => {
        setShowConfirmModal(false);
        // Revert display to what it was before the user tried to change it
        setDisplayTemplateId(displayTemplateIdBeforeChange);
        setPendingTemplateId('');
        setDisplayTemplateIdBeforeChange('');
    };

    const handleContentChange = (newContent: string) => {
        setTemplateContent(newContent);
        updateElement(0, {
             "template_medical_history-template_id": selectedTemplateId,
            "template_medical_history-notes": newContent
        });
    };

    // Memoize the initialValue to prevent new string references on every render
    const memoizedInitialValue = useMemo(() => {
        return templateContent || initialValue || '';
    }, [templateContent, initialValue]);

    // Create a key that changes when template actually changes, forcing remount
    const editorKey = useMemo(() => {
        // Key changes when:
        // 1. Template is selected/changed
        // 2. Initial value is set (first load)
        return selectedTemplateId || (initialValue ? 'initial' : 'empty');
    }, [selectedTemplateId, initialValue]);

    return (
        <div>
            <Modal
                open={showConfirmModal}
                closeModal={handleCancelTemplateChange}
                confirmAction={handleConfirmTemplateChange}
                title={<Translate id="hospital.template_medical_history_confirm_change_title" />}
            >
                <Typography variant="body2">
                    <Translate id="hospital.template_medical_history_confirm_change_message" />
                </Typography>
            </Modal>

            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="template-select-label"><Translate id="hospital.template_medical_history_select_template" /></InputLabel>
                <Select
                    labelId="template-select-label"
                    id="template-select"
                    value={displayTemplateId}
                    label={<Translate id="hospital.template_medical_history_select_template" />}
                    onChange={handleTemplateChange}
                    disabled={loadingTemplates}
                >
                    {templates?.map(template => (
                        <MenuItem key={template.uuid} value={template.uuid}>
                            {template.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {(selectedTemplateId || templateContent || initialValue) && (
                <MedicalHistoryEditor
                    key={editorKey}
                    initialValue={memoizedInitialValue}
                    loading={isLoading}
                    onChange={handleContentChange}
                    onBlur={onBlur}
                />
            )}
        </div>
    );
};

export default TemplatedMedicalHistoryEditor; 